import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatBytes, formatDate, formatMoney } from '../ui/format';
import { Badge } from '../ui/Page';
import { MemberSelect } from '../ui/UserSelect';
import {
  getCustomer360,
  relatedFiles,
  relatedTasks,
  updateRecord,
  type CrmEntity,
  type CrmEntityType,
  type RecordDetail,
} from './detail-api';

/**
 * A section of the right-hand column, collapsed until somebody wants it.
 *
 * The count is in the header on purpose: "Negócios (0)" answers the question without opening
 * anything, which is what most of these are asked for. A section that has to be opened to say
 * "nothing here" is a section that gets opened once per record for ever.
 */
export function RelatedSection({
  title,
  count,
  children,
  open: initiallyOpen = false,
}: {
  title: string;
  count?: number;
  children: ReactNode;
  open?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(initiallyOpen);

  return (
    <section className="related">
      <button
        type="button"
        className="related__head"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="related__chevron" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span className="strong">{title}</span>
        {count !== undefined && <span className="related__count">{count}</span>}
      </button>

      {open && <div className="related__body">{children}</div>}
    </section>
  );
}

/**
 * Everything attached to this record.
 *
 * Each list is its own query and only runs when its section is opened — a record screen that fetched
 * six collections on load would be six requests for information most visits never look at.
 */
export function RecordSide({
  entity,
  entityType,
  id,
  record,
}: {
  entity: CrmEntity;
  entityType: CrmEntityType;
  id: string;
  record: RecordDetail;
}): React.JSX.Element {
  const companyId = entity === 'companies' ? id : ((record.company_id as string | null) ?? null);

  const view = useQuery({
    queryKey: ['customer360', companyId],
    queryFn: () => getCustomer360(companyId!),
    enabled: Boolean(companyId),
  });

  const tasks = useQuery({
    queryKey: ['related-tasks', entityType, id],
    queryFn: () => relatedTasks(entityType, id),
  });

  const files = useQuery({
    queryKey: ['related-files', entityType, id],
    queryFn: () => relatedFiles(entityType, id),
  });

  const contacts = view.data?.contacts ?? [];
  const deals = (view.data?.opportunities ?? []) as RecordDetail[];

  return (
    <div className="record__related">
      <Owner entity={entity} id={id} ownerId={(record.owner_id as string | null) ?? null} />

      {companyId && entity !== 'companies' && (
        <RelatedSection title={t('record.company')} count={1} open>
          <Link to={`/crm/registro/companies/${companyId}`}>
            {(view.data?.company.name as string) ?? t('record.open')}
          </Link>
        </RelatedSection>
      )}

      {companyId && (
        <RelatedSection title={t('record.deals')} count={deals.length}>
          {deals.length === 0 ? (
            <p className="muted small">{t('record.noDeals')}</p>
          ) : (
            <ul className="home-list">
              {deals.map((deal) => (
                <li key={String(deal.id)}>
                  <Link to={`/crm/negocios/${String(deal.id)}`}>{String(deal.title)}</Link>
                  <span className="cell-inline">
                    <span className="strong">{formatMoney(Number(deal.amount_cents ?? 0))}</span>
                    <Badge tone={dealTone(String(deal.status))}>
                      {t(`crm.deal.${String(deal.status)}`)}
                    </Badge>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </RelatedSection>
      )}

      {entity === 'companies' && (
        <RelatedSection title={t('record.contacts')} count={contacts.length}>
          {contacts.length === 0 ? (
            <p className="muted small">{t('record.noContacts')}</p>
          ) : (
            <ul className="home-list">
              {contacts.map((contact) => (
                <li key={String(contact.id)}>
                  <Link to={`/crm/registro/contacts/${String(contact.id)}`}>
                    {[contact.first_name, contact.last_name]
                      .filter((part): part is string => typeof part === 'string' && part !== '')
                      .join(' ')}
                  </Link>
                  {typeof contact.job_title === 'string' && contact.job_title !== '' && (
                    <span className="muted small">{contact.job_title}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </RelatedSection>
      )}

      <RelatedSection title={t('record.tasks')} count={tasks.data?.tasks.length}>
        {(tasks.data?.tasks.length ?? 0) === 0 ? (
          <p className="muted small">{t('record.noTasks')}</p>
        ) : (
          <ul className="home-list">
            {(tasks.data?.tasks ?? []).map((task) => (
              <li key={task.id}>
                <Link to={`/trabalho/tarefas/${task.id}`}>{task.title}</Link>
                <span className="cell-inline">
                  <Badge tone={task.status === 'DONE' ? 'positive' : 'neutral'}>
                    {t(`work.task.${task.status}`)}
                  </Badge>
                  {task.due_date && (
                    <span className="muted small">{formatDate(task.due_date)}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </RelatedSection>

      <RelatedSection title={t('record.files')} count={files.data?.files.length}>
        {(files.data?.files.length ?? 0) === 0 ? (
          <p className="muted small">{t('record.noFiles')}</p>
        ) : (
          <ul className="home-list">
            {(files.data?.files ?? []).map((file) => (
              <li key={file.id}>
                <span>{file.name}</span>
                <span className="muted small">{formatBytes(file.sizeBytes)}</span>
              </li>
            ))}
          </ul>
        )}
      </RelatedSection>
    </div>
  );
}

/**
 * Who this record belongs to.
 *
 * Its own control rather than a row in the field list, because it is the field people change most
 * and the one a record with nobody on it is worst at: an unowned lead is a lead nobody answers.
 */
function Owner({
  entity,
  id,
  ownerId,
}: {
  entity: CrmEntity;
  id: string;
  ownerId: string | null;
}): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const assign = useMutation({
    mutationFn: (next: string) => updateRecord(entity, id, { ownerId: next || null }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['record', entity, id] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <div className="record__owner">
      <div className="field">
        <span>{t('crm.owner')}</span>
        <MemberSelect
          value={ownerId}
          disabled={assign.isPending}
          placeholder={t('crm.unassigned')}
          onChange={(next) => assign.mutate(next ?? '')}
        />
      </div>

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function dealTone(status: string): 'positive' | 'danger' | 'info' {
  if (status === 'WON') return 'positive';
  if (status === 'LOST') return 'danger';

  return 'info';
}
